import React, { useState, useCallback, useEffect, useRef, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import { OrangeCap } from "./NormalSearch";
import { MODES, ModeTrigger, ModeMenu } from "./ModeSelector";
import SuggestionsDropdown from "./SuggestionsDropdown";
import useClickOutside from "./hooks/useClickOutside";
import useDebouncedValue from "./hooks/useDebouncedValue";
import {
    getManualSuggestions,
    getAiSuggestions,
    getRecentLocationSearches,
    pushRecentLocationSearch,
} from "./searchSuggestions";
import { ClipIcon, MicIcon, SendIcon, SearchIcon } from "./icons";

const DEBOUNCE_MS = 350;

function UnifiedSearch({ onSearch, defaultMode = "ai" }) {
    const [mode, setMode] = useState(defaultMode);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [recentSearches, setRecentSearches] = useState(getRecentLocationSearches);

    const [modeMenuOpen, setModeMenuOpen] = useState(false);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Stable id so the input can point aria-activedescendant/aria-controls
    // at the listbox and its options without colliding if multiple search
    // bars are ever rendered on the same page.
    const [listboxId] = useState(
        () => `search-suggestions-${Math.random().toString(36).slice(2, 9)}`
    );

    const outerRef = useRef(null);
    const modeTriggerRef = useRef(null);
    const modeMenuRef = useRef(null);
    const suggestionsRef = useRef(null);
    const inputRef = useRef(null);
    const blurTimerRef = useRef(null);
    const abortControllerRef = useRef(null);

    const navigate = useNavigate();
    const active = MODES[mode];
    const debouncedText = useDebouncedValue(text, DEBOUNCE_MS);

    // ── Cancel any in-flight search request and start tracking a new one.
    // Callers pass the returned signal through to onSearch so a parent-side
    // fetch can be aborted if the person keeps typing or switches mode. ──
    const nextSearchSignal = useCallback(() => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        return controller.signal;
    }, []);

    useEffect(() => () => abortControllerRef.current?.abort(), []);

    // ── Dropdown #2 data: recomputed from mode + query ──
    const suggestions = useMemo(() => {
        if (mode === "manual") return getManualSuggestions(text, recentSearches);
        return getAiSuggestions(text);
    }, [mode, text, recentSearches]);

    const suggestionsHeading = useMemo(() => {
        if (mode === "manual") return text.trim() ? null : "Recent searches";
        return text.trim() ? null : "Try asking";
    }, [mode, text]);

    const activeOptionId =
        highlightedIndex >= 0 && suggestions[highlightedIndex]
            ? `${listboxId}-opt-${suggestions[highlightedIndex].id}`
            : undefined;

    // ── Close whichever dropdown is open on outside click/tap ──
    const closeAllDropdowns = useCallback(() => {
        setModeMenuOpen(false);
        setSuggestionsOpen(false);
    }, []);
    useClickOutside(outerRef, closeAllDropdowns, modeMenuOpen || suggestionsOpen);

    // ── Live filter as you type in manual mode ──
    useEffect(() => {
        if (mode !== "manual") return;
        const trimmed = debouncedText.trim();
        const signal = nextSearchSignal();
        onSearch?.(trimmed || null, { signal });
    }, [debouncedText, mode, onSearch, nextSearchSignal]);

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [suggestions]);

    useEffect(() => () => clearTimeout(blurTimerRef.current), []);

    // AI search no longer checks auth here — AiSearchPage renders
    // AuthRequiredGate itself if there's no session, so this can just
    // navigate unconditionally and let the destination page decide.
    const runAiSearch = useCallback((query) => {
        const q = query.trim();
        if (!q || loading) return;
        setLoading(true);
        setSuggestionsOpen(false);
        const signal = nextSearchSignal();
        setTimeout(() => {
            if (signal.aborted) return;
            navigate("/ai-search", { state: { question: q } });
            setText("");
            setLoading(false);
        }, 120);
    }, [loading, navigate, nextSearchSignal]);

    const runManualSearch = useCallback((query) => {
        const q = query.trim();
        if (!q) return;
        setSuggestionsOpen(false);
        setRecentSearches(pushRecentLocationSearch(q));
        const signal = nextSearchSignal();
        onSearch?.(q, { signal });
    }, [onSearch, nextSearchSignal]);

    const handleSearch = useCallback(() => {
        if (mode === "ai") runAiSearch(text);
        else runManualSearch(text);
    }, [mode, text, runAiSearch, runManualSearch]);

    const handleSelectSuggestion = useCallback((item) => {
        setText(item.label);
        inputRef.current?.focus();
        if (mode === "manual") runManualSearch(item.label);
        else setSuggestionsOpen(false); // let the person review/edit an AI prompt before sending
    }, [mode, runManualSearch]);

    const handleModeChange = useCallback((nextMode) => {
        abortControllerRef.current?.abort();
        setMode(nextMode);
        setModeMenuOpen(false);
        setSuggestionsOpen(false);
        setText("");
        if (nextMode === "manual") onSearch?.(null);
        inputRef.current?.focus();
    }, [onSearch]);

    const handleClear = useCallback(() => {
        setText("");
        if (mode === "manual") {
            const signal = nextSearchSignal();
            onSearch?.(null, { signal });
        }
        inputRef.current?.focus();
    }, [mode, onSearch, nextSearchSignal]);

    const handleInputFocus = useCallback(() => {
        clearTimeout(blurTimerRef.current);
        setModeMenuOpen(false);
        setSuggestionsOpen(true);
    }, []);

    const handleInputBlur = useCallback(() => {
        // small delay so a click on a suggestion registers before the list unmounts
        blurTimerRef.current = setTimeout(() => setSuggestionsOpen(false), 120);
    }, []);

    const handleInputKeyDown = useCallback((e) => {
        if (!suggestionsOpen || suggestions.length === 0) {
            if (e.key === "Enter") handleSearch();
            return;
        }
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlightedIndex((i) => (i + 1) % suggestions.length);
                break;
            case "ArrowUp":
                e.preventDefault();
                setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
                break;
            case "Enter":
                if (highlightedIndex >= 0) {
                    e.preventDefault();
                    handleSelectSuggestion(suggestions[highlightedIndex]);
                } else {
                    handleSearch();
                }
                break;
            case "Escape":
                setSuggestionsOpen(false);
                break;
            default:
                break;
        }
    }, [suggestionsOpen, suggestions, highlightedIndex, handleSearch, handleSelectSuggestion]);

    const toggleModeMenu = useCallback(() => {
        setSuggestionsOpen(false);
        setModeMenuOpen((v) => !v);
    }, []);

    return (
        <div className="search-pill-outer" ref={outerRef}>
            <div className={`search-pill unified${mode === "ai" ? " ai" : ""}`}>
                <ModeTrigger
                    mode={mode}
                    open={modeMenuOpen}
                    onToggle={toggleModeMenu}
                    triggerRef={modeTriggerRef}
                />

                <div className="pill-content">
                    <div className="input-wrapper">
                        <input
                            ref={inputRef}
                            className="pill-input"
                            placeholder={active.placeholder}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            disabled={loading}
                            autoComplete="off"
                            aria-label={active.ariaLabel}
                            role="combobox"
                            aria-expanded={suggestionsOpen}
                            aria-autocomplete="list"
                            aria-controls={suggestionsOpen ? listboxId : undefined}
                            aria-activedescendant={suggestionsOpen ? activeOptionId : undefined}
                        />
                        {text && !loading && (
                            <button className="clear-btn" onClick={handleClear} aria-label="Clear" type="button">
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {mode === "ai" ? (
                    <OrangeCap height={58} width={155} className="ai-cap">
                        <button className="ai-action-btn" type="button" aria-label="Attach file">
                            <ClipIcon />
                        </button>
                        <button className="ai-action-btn" type="button" aria-label="Voice input">
                            <MicIcon />
                        </button>
                        <button className="ai-action-btn" type="button" aria-label="Send" onClick={handleSearch} disabled={loading}>
                            {loading ? <span className="loader-dots">···</span> : <SendIcon />}
                        </button>
                    </OrangeCap>
                ) : (
                    <OrangeCap height={58}>
                        <button className="pill-btn" onClick={handleSearch} type="button" aria-label="Search">
                            <SearchIcon />
                        </button>
                    </OrangeCap>
                )}
            </div>

            {modeMenuOpen && (
                <ModeMenu mode={mode} onSelect={handleModeChange} menuRef={modeMenuRef} />
            )}

            {suggestionsOpen && (
                <SuggestionsDropdown
                    listboxId={listboxId}
                    items={suggestions}
                    highlightedIndex={highlightedIndex}
                    onHover={setHighlightedIndex}
                    onSelect={handleSelectSuggestion}
                    heading={suggestionsHeading}
                    dropdownRef={suggestionsRef}
                />
            )}
        </div>
    );
}

export default memo(UnifiedSearch);